<template>
  <div class="md-mask" v-if="show">
    <div class="md-container">
      <div class="md-dialog-header">
        <div class="md-header-content">
          <div class="icon-pill">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="width:16px;height:16px;"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
          </div>
          <div>
            <h3 class="md-title">Feedback Settings</h3>
            <p class="md-subtitle">Runner: {{ runnerName }}</p>
          </div>
        </div>
        <button class="md-close-btn" @click="close">×</button>
      </div>
      
      <div class="md-dialog-body">
        <div class="md-setting-row">
          <div>
            <div class="md-label">Feedback Widget Enabled</div>
            <div class="md-desc">Allows advanced crash and feedback reporting for this user.</div>
          </div>
          <lv-toggle v-model="enabled" @change="toggleChanged"/>
        </div>
        <div v-if="error" class="md-error">
          {{ error }}
        </div>
      </div>
      
      <div class="md-dialog-footer">
        <button class="md-ghost-btn" @click="close">Cancel</button>
        <button class="md-primary-btn" @click="saveSettings" :disabled="loading">
          {{ loading ? 'Saving...' : 'Save Settings' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import LvToggle from '@/components/Forms/LvToggle'
import { db } from '@/firebase'

export default {
  name: 'BugseeSettingsModal',
  components: { LvToggle },
  props: {
    show: { type: Boolean, default: false },
    runnerId: { type: [Number, String], required: true },
    runnerName: { type: String, default: '' }
  },
  data() {
    return {
      enabled: false,
      loading: false,
      error: ''
    }
  },
  watch: {
    async show(val) {
      if (val) {
        this.error = ''
        await this.loadSettings()
      }
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    toggleChanged(val) {
      if(val !== undefined) this.enabled = val;
    },
    async loadSettings() {
      if (!this.runnerId) return;
      this.loading = true;
      try {
        const ref = db.ref(`/user_settings/${this.runnerId}/feedback_widget_enabled`);
        const snapshot = await ref.once('value');
        this.enabled = !!snapshot.val();
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    async saveSettings() {
      this.loading = true;
      this.error = '';
      try {
        if (this.enabled) {
          // Check if < 10 users enabled
          const allSettingsRef = db.ref(`/user_settings`);
          const snap = await allSettingsRef.once('value');
          const allSettings = snap.val() || {};
          let count = 0;
          for (let key in allSettings) {
            if (Object.prototype.hasOwnProperty.call(allSettings, key)) {
                if (allSettings[key].feedback_widget_enabled === true && String(key) !== String(this.runnerId)) {
                count++;
                }
            }
          }
          if (count >= 10) {
            this.error = 'Maximum limit of 10 users reached. Please disable another user first.';
            this.loading = false;
            return;
          }
        }
        
        await db.ref(`/user_settings/${this.runnerId}/feedback_widget_enabled`).set(this.enabled);
        this.$emit('saved');
        this.close();
      } catch (err) {
        console.error('Failed to change feedback settings:', err);
        this.error = 'Failed to update settings. Please try again.';
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.md-mask {
  position: fixed; z-index: 9998; top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.5); display: flex; justify-content: center; align-items: center;
}
.md-container {
  width: 480px; background-color: #fff; border-radius: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); display: flex; flex-direction: column;
}
.md-dialog-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 1.25rem 1.5rem; border-bottom: 1px solid #f0f0f5;
}
.md-header-content { display: flex; align-items: center; gap: 12px; }
.icon-pill {
  width: 36px; height: 36px; border-radius: 10px; background: #f0effe; color: #5b4fcf;
  display: flex; justify-content: center; align-items: center;
}
.md-title { font-size: 16px; font-weight: 700; color: #1a1a2e; margin: 0; }
.md-subtitle { font-size: 12px; color: #8a8a9a; margin: 0; }
.md-close-btn {
  width: 30px; height: 30px; border: 1px solid #e8e8ed; background: transparent; cursor: pointer;
  border-radius: 8px; font-size: 20px; display: flex; justify-content: center; align-items: center; color: #aaa;
}
.md-close-btn:hover { color: #c0392b; border-color: #fdd; background: #fff5f5; }
.md-dialog-body { padding: 1.5rem 1.75rem; display: flex; flex-direction: column; gap: 1.1rem; }
.md-setting-row { display: flex; justify-content: space-between; align-items: center; }
.md-label { font-size: 13px; font-weight: 600; color: #1a1a2e; margin-bottom: 4px; }
.md-desc { font-size: 11px; color: #8a8a9a; }
.md-error { background: #fff5f5; border: 1px solid #fdd; border-radius: 7px; padding: 7px 12px; font-size: 12px; color: #c0392b; margin-top: 10px; }
.md-dialog-footer {
  border-top: 1px solid #f0f0f5; padding: 1rem 1.75rem 1.5rem; display: flex; justify-content: flex-end; gap: 10px;
}
.md-primary-btn {
  background: #5b4fcf; color: #fff; border: none; height: 36px; padding: 0 18px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer;
}
.md-primary-btn:hover { background: #4338ca; }
.md-primary-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.md-ghost-btn {
  border: 1.5px solid #e8e8ed; background: #fff; color: #1a1a2e; height: 36px; padding: 0 16px; border-radius: 7px; font-size: 13px; font-weight: 600; cursor: pointer;
}
.md-ghost-btn:hover { border-color: #d0d0e0; }
</style>



// WEBPACK FOOTER //
// BugseeSettingsModal.vue?ceb2b902